import { stringComparer } from "libs/comparers";
import { uniqWith } from "lodash-comms";
import { ClientEnvironment } from "~/environment/ClientEnvironment";
import { GetOptions } from "~/environment/RecordLoader";
import { RecordValue } from "libs/schema";

export type GetMessagesScheduledToBeSentResult = RecordValue<"message">[];

/**
 * A message is considered to be "scheduled to be sent" if any of the following are true:
 * - it has a `scheduled_to_be_sent_at` value which is in the future
 * - it has a `server_updated_at` value which is null
 * - it has a `last_edited_at` value which is greater than the `server_updated_at` value.
 *
 * Result is ordered by `sent_at`, then `scheduled_to_be_sent_at`, then `id`.
 */
export async function getMessagesScheduledToBeSent(
  environment: ClientEnvironment,
  options?: GetOptions,
): Promise<GetMessagesScheduledToBeSentResult> {
  const currentUserId = environment.auth.getAndAssertCurrentUserId();
  const currentTimestamp = new Date().toISOString();

  // Fetch both pending and scheduled messages in parallel
  const [[pendingMessages], [scheduledMessages]] = await Promise.all([
    // Messages not yet sent to server
    environment.recordLoader.getPendingSentMessages({ currentUserId }, { fetchStrategy: "cache" }),
    // Messages scheduled to be sent after the current timestamp
    environment.recordLoader.getSentMessages({ userId: currentUserId, sentAfter: currentTimestamp }, options),
  ]);

  // Combine and filter messages based on criteria
  const result = [...pendingMessages, ...scheduledMessages]
    .filter((message) => {
      const isFutureScheduled = message.scheduled_to_be_sent_at > currentTimestamp;
      const isServerUpdateMissing = !message.server_updated_at;
      const isEditedAfterServerUpdate =
        !!message.last_edited_at && !!message.server_updated_at && message.last_edited_at > message.server_updated_at;

      return isFutureScheduled || isServerUpdateMissing || isEditedAfterServerUpdate;
    })
    .sort(
      (a, b) =>
        stringComparer(a.sent_at, b.sent_at) ||
        stringComparer(a.scheduled_to_be_sent_at, b.scheduled_to_be_sent_at) ||
        stringComparer(a.id, b.id),
    );

  return uniqWith(result, (m1, m2) => m1.id === m2.id);
}
